// src/components/common/ExpandableDataTable.jsx
import React from 'react';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronRight as ChevronRightIcon } from 'lucide-react';

const ExpandableDataTable = ({ 
  columns,
  data = [],
  onSort,
  sortConfig,
  currentPage = 0,
  pageSize = 10,
  totalElements = 0,
  onPageChange,
  onPageSizeChange,
  isLoading = false,
  disabled = false,
  expandedRows,
  expandedContent,
  rowClassName = '',
  onToggleExpand // New prop for toggle callback
}) => {
  // Hitung total pages
  const totalPages = Math.ceil(totalElements / pageSize);
  
  // Hitung range data yang ditampilkan
  const startIndex = (currentPage * pageSize) + 1;
  const endIndex = Math.min((currentPage + 1) * pageSize, totalElements);

  // Handle sort click
  const handleSortClick = (key) => {
    if (!onSort || !sortConfig) return;
    const direction = sortConfig.sortBy === key && sortConfig.direction === 'ASC' ? 'DESC' : 'ASC';
    onSort({ sortBy: key, direction });
  };

  // Render sort indicator
  const renderSortIndicator = (column) => {
    if (!sortConfig || !column.sortable) return null;
    if (sortConfig.sortBy !== column.key) {
      return <span className="text-white opacity-50">↕</span>;
    }
    return sortConfig.direction === 'ASC' ? 
      <span className="text-white font-bold">↑</span> : 
      <span className="text-white font-bold">↓</span>;
  };

  // Handle expand toggle
  const handleToggleExpand = (rowId) => {
    if (onToggleExpand) {
      onToggleExpand(rowId);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          {/* Table Header */}
          <thead className="bg-gradient-to-r from-red-600 to-red-500">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider ${
                    column.sortable ? 'cursor-pointer select-none hover:bg-opacity-90' : ''
                  } ${column.width ? column.width : ''}`}
                  onClick={() => column.sortable && !disabled && handleSortClick(column.key)}
                  style={column.minWidth ? { minWidth: column.minWidth } : {}}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.label}</span>
                    {renderSortIndicator(column)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          {/* Table Body */}
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center space-y-3">
                    <div className="flex space-x-2">
                      <div className="w-3 h-3 bg-red-400 rounded-full animate-bounce"></div>
                      <div className="w-3 h-3 bg-red-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-3 h-3 bg-red-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                    <p className="text-gray-500 text-sm">Memuat data...</p>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <svg className="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <p className="text-gray-500 text-sm font-medium">Tidak ada data</p>
                    <p className="text-gray-400 text-xs">Data akan muncul di sini</p>
                  </div>
                </td>
              </tr>
            ) : (
              data.map((row, index) => {
                const isExpanded = expandedRows?.has(row.id);
                
                return (
                  <React.Fragment key={row.id || index}>
                    {/* Main Row */}
                    <tr className={`hover:bg-red-50 transition-colors ${rowClassName}`}>
                      {columns.map((column) => (
                        <td 
                          key={column.key} 
                          className={`px-6 py-4 ${column.nowrap !== false ? 'whitespace-nowrap' : ''}`}
                        >
                          {column.render ? column.render(row, index) : row[column.key] || '-'}
                        </td>
                      ))}
                    </tr>
                    
                    {/* Expanded Content */}
                    {isExpanded && expandedContent && (
                      <tr>
                        <td colSpan={columns.length} className="p-0">
                          {expandedContent(row)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Section */}
      {totalElements > 0 && (
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0">
            {/* Data Range Info */}
            <div className="text-sm text-gray-700">
              Menampilkan <span className="font-semibold text-red-600">{startIndex}-{endIndex}</span> dari{' '}
              <span className="font-semibold text-red-600">{totalElements}</span> data
            </div>

            {/* Right Section: Page Size & Navigation */}
            <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-4">
              {/* Page Size Selector */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Tampilkan</span>
                <select
                  value={pageSize}
                  onChange={(e) => onPageSizeChange(Number(e.target.value))}
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  disabled={disabled}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm text-gray-700">data</span>
              </div>

              {/* Pagination Controls */}
              <div className="flex items-center space-x-2">
                {/* Current Page Indicator */}
                <span className="px-3 py-1 text-sm text-gray-700">
                  <span className="font-semibold text-red-600">{currentPage + 1}</span>
                  {' / '}
                  <span className="font-semibold">{totalPages}</span>
                </span>

                {/* Previous Page */}
                <button
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={disabled || currentPage === 0}
                  className="p-2 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Halaman Sebelumnya"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                {/* Next Page */}
                <button
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={disabled || currentPage >= totalPages - 1}
                  className="p-2 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Halaman Selanjutnya"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpandableDataTable;